<template>
  <v-col cols="12">
    <v-card>
      <v-card-title class="text-h5"> Insider Buying/Selling </v-card-title>

      <v-card-text>
        <p>
          When multiple officers in a company, particularly the C-Level officers are buying, that's
          usually a good sign they believe the stock price is going to go up. Use the Cluster
          Buy/Sell search to uncover new investment opportunities.
        </p>
        <p>
          The Cluster Buying/Selling Search Engine lets you filter by date range, owner type,
          transaction type, set a minimum number of owners, and set the minimum transaction proceeds
          threshold to find large purchases or sales.
        </p>

        <!-- Options -->
        <v-row mt-6>
          <!-- Transaction Types -->
          <v-col cols="12" sm="6" xl="2" mb-4>
            <helpers-section-sub-header class="mb-2">
              Transaction types:
            </helpers-section-sub-header>
            <v-select
              v-model="clusterCriteria.transaction_type"
              :items="tenFilterTransactions"
              item-title="text"
              hide-details
              class="pt-0"
            />
          </v-col>

          <!-- Date Range -->
          <v-col cols="12" sm="6" xl="2" mb-4>
            <helpers-section-sub-header class="mb-2"> Date range: </helpers-section-sub-header>
            <v-select
              v-model="clusterCriteria.date_range"
              :items="tenFilterDates"
              item-title="text"
              hide-details
              class="pt-0"
            />
          </v-col>

          <!-- Types of Owners -->
          <v-col cols="12" xl="4" mb-4>
            <helpers-section-sub-header class="mb-2"> Types of owners: </helpers-section-sub-header>
            <v-select
              v-model="clusterCriteria.owner_types"
              :items="filterOwnerTypes"
              item-title="text"
              placeholder="Select owner types"
              density="compact"
              multiple
              chips
              closable-chips
              hide-details
              class="pa-0"
            />
          </v-col>

          <v-col cols="12" pa-0 />
          <!-- New line -->

          <!-- Minimum # insiders -->
          <v-col cols="12" sm="6" md="3" lg="3" xl="2" mb-4>
            <helpers-section-sub-header>Minimum # insiders:</helpers-section-sub-header>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="clusterCriteria.minimum_unique_owners"
                  mask="####"
                  hide-details
                  class="pt-0"
                />
              </v-col>
            </v-row>
          </v-col>

          <!-- Min. Cluster Amount -->
          <v-col cols="12" sm="6" md="3" lg="3" xl="2" mb-4>
            <helpers-section-sub-header>Min. cluster amount:</helpers-section-sub-header>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="clusterCriteria.minimum_transaction_amount"
                  type="number"
                  prefix="$"
                  hide-details
                  class="pt-0"
                />
              </v-col>
            </v-row>
          </v-col>

          <!-- Avg. Daily Volume -->
          <v-col cols="12" md="6" lg="6" xl="4" mb-4>
            <helpers-section-sub-header>Avg. daily volume:</helpers-section-sub-header>
            <v-row>
              <v-col cols="6">
                <v-text-field
                  v-model="clusterCriteria.avg_daily_volume_from"
                  type="number"
                  label="From"
                  hide-details
                  class="pt-0"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="clusterCriteria.avg_daily_volume_to"
                  type="number"
                  label="To"
                  hide-details
                  class="pt-0"
                />
              </v-col>
            </v-row>
          </v-col>

          <v-col cols="12" pa-0 />
          <!-- New line -->

          <!-- Sectors -->
          <v-col cols="12" xl="4" mb-2>
            <helpers-section-sub-header class="mb-2">
              Limit to Certain Sectors:
            </helpers-section-sub-header>
            <v-combobox
              v-model="clusterCriteria.sectors"
              :items="sectorNames"
              placeholder="Select sectors"
              multiple
              chips
              closable-chips
              clearable
              density="compact"
              hide-details
              class="pa-0"
            />
          </v-col>

          <!-- Filter by stock market cap -->
          <v-col cols="12" xl="4" mb-2>
            <helpers-section-sub-header class="mb-2">
              Filter by stock market cap:
            </helpers-section-sub-header>
            <v-select
              v-model="clusterCriteria.market_caps"
              :items="marketCaps"
              return-object
              item-title="name"
              placeholder="Select market caps"
              multiple
              density="compact"
              chips
              closable-chips
              clearable
              hide-details
              class="pa-0"
            />
          </v-col>

          <!-- Use 10b5-1 Transactions -->
          <v-col cols="12" mt-2>
            <v-checkbox
              v-model="clusterCriteria.is_10b5"
              label="Use 10b5-1 transactions"
              hide-details
              class="mt-0"
            />
          </v-col>
        </v-row>

        <!-- Table -->
        <v-data-table
          v-model:options="pagination"
          v-model:sort-by="pagination.sortBy"
          :headers="tableHeaders"
          :items="tableItems"
          :items-per-page-options="[10, 25, 50, 100]"
          :loading="loading"
          :no-data-text="loading ? 'Loading...' : 'No results found.'"
          must-sort
          class="mt-6"
        >
          <template #item="{ item }">
            <tr>
              <td>
                <v-btn
                  :class="{ 'mr-4': $vuetify.display.smAndUp }"
                  small
                  title="View Transactions"
                  class="ma-0"
                  @click="$refs.transactionDetailsDialog.showDetails(item, clusterCriteria)"
                >
                  View transactions
                </v-btn>
              </td>
              <td>{{ item.symbol }}</td>
              <td>{{ item.name }}</td>
              <td>{{ item.sector }}</td>
              <td>{{ item.transaction_type }}</td>
              <td>{{ item.count }}</td>
              <td>{{ item.unique_count }}</td>
              <td>{{ whole_number(item.transaction_shares) }}</td>
              <td>{{ currency(item.transaction_amount) }}</td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <transaction-details-dialog ref="transactionDetailsDialog" />
  </v-col>
</template>

<script>
import { useUserStore, useAppStore, useWwStore } from '@/store'
// Modules
import api from '@/store/api'
import { mapFields } from '@/store/helpers'
import cloneDeep from 'lodash/cloneDeep'
import debounce from 'lodash/debounce'

// Mixins
import FormattersMixin from '@/mixins/formatters'

// Components
import TransactionDetailsDialog from '@/components/page/insider-dashboard/_TransactionDetailsDialog'

// Static data
import { defaultInsiderBacktesterClusterCriteria } from '@/data/constants'

export default {
  components: {
    TransactionDetailsDialog
  },
  mixins: [FormattersMixin],

  data() {
    return {
      loading: false,
      tableHeaders: [
        { title: '', key: 'symbol', width: 1, sortable: false },
        { title: 'Ticker', key: 'symbol' },
        { title: 'Name', key: 'name' },
        { title: 'Sector', key: 'sector' },
        { title: 'Type', key: 'transaction_type' },
        { title: '# of Transactions', key: 'count' },
        { title: '# Owners', key: 'unique_count' },
        { title: 'Total Shares', key: 'transaction_shares' },
        { title: 'Total Proceeds', key: 'transaction_amount' }
      ],
      tableItems: [],
      pagination: {
        sortBy: [{ key: 'transaction_amount', order: 'desc' }],
        page: 1,
        itemsPerPage: 10
      },
      tenFilterTransactions: [
        { value: 1, text: 'Show All Transactions' },
        { value: 2, text: 'All Purchases' },
        { value: 3, text: 'All Sells' },
        { value: 4, text: 'Open Market Purchases' },
        { value: 5, text: 'Open Market Sales' },
        { value: 6, text: 'Option Exercises' }
      ],
      tenFilterDates: [
        { value: 0, text: 'Past Day' },
        { value: 1, text: 'Past 3 Days' },
        { value: 2, text: 'Past 7 Days' },
        { value: 6, text: 'Past 2 Weeks' },
        { value: 3, text: 'Past Month' },
        { value: 4, text: 'Past 3 Months' },
        { value: 5, text: 'Past 6 Months' },
        { value: 7, text: 'Past Year' }
      ],
      filterOwnerTypes: [
        { value: 1, text: '>10% Owner' },
        { value: 2, text: 'Director' },
        { value: 3, text: 'Officer' },
        { value: 5, text: 'C-Level Officer (CEO, COO, CFO, etc.)' },
        { value: 4, text: 'Other Owner' }
      ],
      marketCaps: [
        { name: 'Over $200 billion', id: 1 },
        { name: '$100 billiion - $200 billion', id: 2 },
        { name: '$50 billion - $100 billion', id: 3 },
        { name: '$20 billion - $50 billion', id: 4 },
        { name: '$10 billion - $20 billion', id: 5 },
        { name: '$2 billion - $10 billion', id: 6 },
        { name: '$250 million - $2 billion', id: 7 },
        { name: 'Below $250 million', id: 8 }
      ]
    }
  },

  computed: {
    sectorNames() {
      return useWwStore().sectors.map(sector => sector.name)
    },
    ...mapFields({
      clusterCriteria: 'app.insiderBacktesterClusterCriteria'
    })
  },

  watch: {
    clusterCriteria: {
      deep: true,
      handler() {
        this.updateData()
      }
    }
  },

  mounted() {
    //   useWwStore().loadSectors()
  },

  created() {
    // Init with defaults
    this.clusterCriteria = cloneDeep(defaultInsiderBacktesterClusterCriteria)
  },

  methods: {
    updateData: debounce(function () {
      this.loading = true

      api
        .post('form4_agg_query', this.clusterCriteria)
        .then(response => {
          this.tableItems = response.data
        })
        .catch(error => {
          useAppStore().showNotification({
            type: 'error',
            text: (error.response && error.response.data) || error.message
          })
        })
        .then(() => (this.loading = false))
    }, 750)
  }
}
</script>
